import React, { useEffect, useRef } from 'react';

import { useIntl } from 'react-intl';

import SignatureIcon from '@atlaskit/icon-lab/core/signature';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import SharedSalesforceDynamicMultiSelect from '../../../../../common/ui/filters/product-filters/shared-salesforce-dynamic-multi-select';
import type { FilterProps } from '../../../../../common/ui/filters/product-filters/types';
import { useSelectFilter } from '../../../../../controllers/filters/base-select-filter';
import { useIsConfigLoaded } from '../../../../../controllers/store/bootstrap';
import { filter as salesforceStageFilter } from '../../../../../controllers/store/search/filters/salesforce-stage';
import { i18n } from './messages';

const SalesforceStageFilter = (props: FilterProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const [filterData, { loadInitialOptions, setOption, clearOptions }] = useSelectFilter({
		filterId: salesforceStageFilter.id,
	});
	const shouldEagerLoadOptions = fg('rovo-fps-3p-ux-salesforce-dynamic-stage-filter');
	const isConfigLoaded = useIsConfigLoaded();
	// Treat as "loaded" once we have invoked loadInitialOptions with a populated bootstrap
	// config. The effect below waits for isConfigLoaded to flip true before flipping this
	// ref, so a race against the bootstrap config load can no longer poison the filter
	// into a permanent empty state.
	const hasLoadedInitialOptions = useRef(false);

	useEffect(() => {
		if (!shouldEagerLoadOptions || hasLoadedInitialOptions.current) {
			return;
		}
		// Hold off until bootstrap config has loaded so the store can actually
		// resolve a datasource and fetch options.
		if (!isConfigLoaded) {
			return;
		}
		hasLoadedInitialOptions.current = true;
		loadInitialOptions();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [shouldEagerLoadOptions, isConfigLoaded]);

	if (!filterData || !shouldEagerLoadOptions) {
		return null;
	}

	const { isLoading, suggestedOptions, selectedOptions } = filterData;

	return (
		<SharedSalesforceDynamicMultiSelect
			{...props}
			filterId={salesforceStageFilter.id}
			label={formatMessage(i18n.salesforceStageFilterLabel)}
			noOptionsMessage={formatMessage(i18n.salesforceStageFilterNoOptions)}
			iconBefore={<SignatureIcon label="" />}
			isLoading={isLoading}
			selectedOptions={selectedOptions}
			suggestedOptions={suggestedOptions}
			loadInitialOptions={loadInitialOptions}
			setOption={(option) => setOption(option)}
			clearOptions={clearOptions}
		/>
	);
};

export default SalesforceStageFilter;
